import { useRef, useState } from "react";
import { useRealtimeInvoices, InvoiceGroup } from "@/lib/useRealtimeInvoices";
import { uploadInvoiceFiles, updateInvoiceGroupAmount } from "@/lib/uploadInvoiceFile";
import { EditableFileName } from "./EditableFileName";
import { useToast } from "@/lib/useToast";
import { Toast } from "@/components/ui";
import { supabase } from "@/lib/supabaseClient";

import { ArchiveDocumentButton, ArchivedDocuments } from "./ArchivedDocuments";
import { setDocumentsArchived } from "@/lib/documentArchive";

interface InvoiceUploadProps {
  caseId: string;
}

function formatDateTime(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

interface InvoiceSectionProps {
  caseId: string;
  invoiceType: "deposit" | "final";
  title: string;
  emoji: string;
}

function InvoiceSection({ caseId, invoiceType, title, emoji }: InvoiceSectionProps) {
  const { groupedInvoices, archivedGroups, loading, refresh } = useRealtimeInvoices(caseId, invoiceType);
  const [archiving, setArchiving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [editingAmount, setEditingAmount] = useState<string | null>(null);
  const [tempAmount, setTempAmount] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast, showToast, hideToast } = useToast();

  const changeArchive = async (ids: string[], archived: boolean) => {
    setArchiving(true);
    try {
      await setDocumentsArchived("invoice", ids, archived);
      await refresh();
      showToast({ message: archived ? "Facture archivée." : "Facture restaurée.", type: "success" });
    } catch (error) {
      showToast({ message: error instanceof Error ? error.message : "Modification impossible.", type: "error" });
    } finally { setArchiving(false); }
  };

  const handleUpdateDisplayName = async (invoiceId: string, newName: string) => {
    const { error } = await supabase
      .from("invoices")
      .update({ display_name: newName })
      .eq("id", invoiceId);

    if (error) throw error;
  };

  const handleUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    // Vérifier que ce sont des PDFs
    const pdfFiles = Array.from(files).filter((file) => file.type === "application/pdf");

    if (pdfFiles.length === 0) {
      showToast({ message: "Veuillez sélectionner uniquement des fichiers PDF", type: "warning" });
      return;
    }

    if (pdfFiles.length !== files.length) {
      showToast({ 
        message: `${files.length - pdfFiles.length} fichier(s) ignoré(s) (non-PDF)`, 
        type: "info" 
      });
    }

    setUploading(true);

    const result = await uploadInvoiceFiles(caseId, invoiceType, pdfFiles);

    if (result.success) {
      showToast({
        message: `${pdfFiles.length} facture${pdfFiles.length > 1 ? 's' : ''} uploadée${pdfFiles.length > 1 ? 's' : ''} avec succès`,
        type: "success"
      });

      // Extraire le montant HT du PDF de la facture (fire-and-forget)
      if (result.uploadGroupId) {
        supabase.auth.getSession().then(({ data: sessionData }) => {
          const token = sessionData.session?.access_token;
          if (token) {
            fetch("/api/invoices/extract-amount", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                uploadGroupId: result.uploadGroupId,
                caseId,
              }),
            }).catch(() => {}); // Fire-and-forget
          }
        });
      }
    } else {
      showToast({ message: `Erreur lors de l'upload: ${result.error}`, type: "error" });
    }

    setUploading(false);

    // Reset l'input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleUpload(e.dataTransfer.files);
    }
  };

  const handleAmountEdit = (group: InvoiceGroup) => {
    setEditingAmount(group.groupId);
    setTempAmount(group.amount?.toString() || "");
  };

  const handleAmountSave = async (groupId: string) => {
    const amount = parseFloat(tempAmount);
    if (isNaN(amount) || amount <= 0) {
      showToast({ message: "Veuillez entrer un montant valide", type: "warning" });
      return;
    }

    const result = await updateInvoiceGroupAmount(groupId, amount);

    if (result.success) {
      setEditingAmount(null);
      setTempAmount("");
      showToast({ message: "Montant mis à jour avec succès", type: "success" });
    } else {
      showToast({ message: `Erreur lors de la mise à jour: ${result.error}`, type: "error" });
    }
  };

  const handleAmountCancel = () => {
    setEditingAmount(null);
    setTempAmount("");
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900">
        {emoji} {title}
      </h3>

      {/* Zone d'upload */}
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          dragActive
            ? "border-indigo-500 bg-indigo-50"
            : uploading
            ? "border-gray-300 bg-gray-50"
            : "border-gray-300 hover:border-indigo-400"
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        {uploading ? (
          <div className="space-y-2">
            <div className="text-4xl">⏳</div>
            <p className="text-sm text-gray-600">Upload en cours...</p>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-4xl">📄</div>
            <div>
              <p className="text-sm font-medium text-gray-700">
                Glissez-déposez vos PDFs ici
              </p>
              <p className="text-xs text-gray-500 mt-1">ou</p>
            </div>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Sélectionner des fichiers
            </button>
            <p className="text-xs text-gray-400">
              Vous pouvez sélectionner plusieurs PDFs
            </p>
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf"
          multiple
          onChange={(e) => handleUpload(e.target.files)}
          className="hidden"
        />
      </div>

      {/* Liste des versions */}
      <div>
        <h4 className="text-sm font-semibold text-gray-900 mb-2">
          Historique des versions ({groupedInvoices.length})
        </h4>
        {loading ? (
          <div className="text-sm text-gray-500 text-center py-4">
            Chargement...
          </div>
        ) : groupedInvoices.length === 0 ? (
          <div className="text-sm text-gray-500 text-center py-4">
            Aucune facture uploadée
          </div>
        ) : (
          <div className="space-y-3">
            {groupedInvoices.map((group) => {
              const firstInvoice = group.invoices[0];

              return (
                <div
                  key={group.groupId}
                  className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-3"
                >
                {/* Header de la version */}
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="text-2xl flex-shrink-0">💰</div>
                    <div className="flex-1 min-w-0">
                      {/* Nom éditable - utiliser le premier invoice du groupe */}
                      <div className="mb-1">
                        <EditableFileName
                          initialName={firstInvoice?.display_name || `Version ${group.version}`}
                          onSave={(newName) => {
                            if (firstInvoice) {
                              return handleUpdateDisplayName(firstInvoice.id, newName);
                            }
                            return Promise.resolve();
                          }}
                        />
                        {group.invoices.length > 1 && (
                          <span className="ml-2 text-xs text-gray-500">
                            ({group.invoices.length} fichiers)
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">
                        {formatDateTime(group.createdAt)}
                      </div>
                    </div>
                  </div>
                </div>

                <ArchiveDocumentButton busy={archiving} onArchive={() => changeArchive(group.invoices.map((invoice) => invoice.id), true)} />

                {/* Montant */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Montant :</span>
                  {editingAmount === group.groupId ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={tempAmount}
                        onChange={(e) => setTempAmount(e.target.value)}
                        className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="0.00"
                        autoFocus
                      />
                      <span className="text-sm text-gray-600">€</span>
                      <button
                        onClick={() => handleAmountSave(group.groupId)}
                        className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                      >
                        ✓
                      </button>
                      <button
                        onClick={handleAmountCancel}
                        className="px-2 py-1 text-xs bg-gray-400 text-white rounded hover:bg-gray-500"
                      >
                        ✗
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-gray-900">
                        {group.amount ? `${group.amount}€` : "Non renseigné"}
                      </span>
                      <button
                        onClick={() => handleAmountEdit(group)}
                        className="text-xs text-indigo-600 hover:text-indigo-800 hover:underline"
                      >
                        Modifier
                      </button>
                    </div>
                  )}
                </div>

                {/* Liste des fichiers */}
                <div className="space-y-1">
                  {group.invoices.map((invoice, index) => (
                    <div
                      key={invoice.id}
                      className="flex items-center justify-between p-2 bg-white rounded border border-gray-100"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">
                          {group.invoices.length > 1 ? `${index + 1}.` : ""}
                        </span>
                        <span className="text-sm text-gray-700">PDF</span>
                      </div>
                      <a
                        href={invoice.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1 text-xs font-medium text-indigo-600 hover:text-indigo-800 hover:underline"
                      >
                        Télécharger
                      </a>
                    </div>
                  ))}
                </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <ArchivedDocuments title={invoiceType === "deposit" ? "Factures d’acompte archivées" : "Factures finales archivées"} busy={archiving}
        documents={archivedGroups.map((group) => ({ id: group.groupId, ids: group.invoices.map((invoice) => invoice.id), name: group.invoices[0]?.display_name || `Facture v${group.version}`, version: group.version, archivedAt: group.invoices[0].archived_at!, urls: group.invoices.map((invoice) => invoice.file_url), status: group.invoices.every((invoice) => invoice.status === "paid") ? "Payée" : undefined }))}
        onRestore={(ids) => changeArchive(ids, false)} />
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
        duration={toast.duration}
      />
    </div>
  );
}

export function InvoiceUpload({ caseId }: InvoiceUploadProps) {
  return (
    <div className="p-6 space-y-8">
      {/* Section Facture d'acompte */}
      <InvoiceSection
        caseId={caseId}
        invoiceType="deposit"
        title="Facture d'acompte (60%)"
        emoji="💳"
      />

      {/* Séparateur */}
      <div className="border-t border-gray-200" />

      {/* Section Facture finale */}
      <InvoiceSection
        caseId={caseId}
        invoiceType="final"
        title="Facture finale (solde)"
        emoji="🧾"
      />
    </div>
  );
}
